// hooks/useComponentHeightWithRef.js

import { useState, useLayoutEffect, useCallback } from 'react';

/**
 * Một React Hook tùy chỉnh để theo dõi chiều cao của một DOM element.
 * Nó nhận vào một React ref và trả về chiều cao hiện tại của element đó.
 * Hook sẽ tự động cập nhật và trả về chiều cao mới mỗi khi element thay đổi kích thước.
 * * @param {React.RefObject<HTMLElement>} elementRef - React ref được gắn vào DOM element cần đo.
 * @returns {number} - Chiều cao hiện tại của element (tính bằng pixel).
 */
export const useComponentHeightWithRef = (elementRef) => {
  // State để lưu trữ chiều cao của component
  const [height, setHeight] = useState(0);

  // Chúng ta sử dụng useLayoutEffect để đo đạc ngay sau khi DOM đã được cập nhật
  // nhưng trước khi trình duyệt kịp "vẽ" lại màn hình. Điều này giúp tránh hiện tượng
  // giao diện bị "nhấp nháy" (flicker) do cập nhật chiều cao.
  useLayoutEffect(() => {
    // Lấy DOM node thực tế từ ref.
    // Trong lần render đầu tiên, `elementRef.current` có thể là null.
    const element = elementRef.current;

    // Nếu không có element, không làm gì cả.
    if (!element) {
      return;
    }

    // 1. Mặc định mục tiêu cần đo là chính thẻ cha (ref)
    let targetToObserve = element;
    // 2. Lấy phần tử con đầu tiên (bỏ qua text node, comment...)
    const firstChild = element.firstElementChild;

    if (firstChild) {
      // 3. Kiểm tra Computed Style của con
      const childStyle = window.getComputedStyle(firstChild);
      // 4. Nếu position là 'fixed', đổi mục tiêu đo sang con
      if (childStyle.position === 'fixed') {
        targetToObserve = firstChild;
      }
    }
    //
    // ResizeObserver là API hiện đại của trình duyệt cho phép chúng ta theo dõi
    // sự thay đổi kích thước của một element một cách hiệu quả.
    const resizeObserver = new ResizeObserver(entries => {
      // entries là một mảng các đối tượng quan sát, chúng ta chỉ cần cái đầu tiên.
      const entry = entries[0];
      if (entry) {
        // Lấy chiều cao từ 'borderBoxSize' để có kết quả chính xác nhất,
        // bao gồm cả padding và border. Fallback về 'contentRect.height' cho các trình duyệt cũ hơn.
        const newHeight = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        
        // Cập nhật state với chiều cao mới.
        // React sẽ chỉ render lại nếu giá trị mới khác giá trị cũ.
        setHeight(newHeight);
      }
    });

    // Bắt đầu quan sát element được tham chiếu bởi ref.
    resizeObserver.observe(targetToObserve);

    // Hàm dọn dẹp (Cleanup Function): CỰC KỲ QUAN TRỌNG.
    // Hàm này sẽ được gọi khi component bị unmount (gỡ bỏ khỏi DOM)
    // hoặc khi ref thay đổi. Nó sẽ ngừng việc quan sát để tránh rò rỉ bộ nhớ (memory leaks).
    return () => {
      resizeObserver.disconnect();
    };
  }, [elementRef]); // Dependency array: Effect này sẽ chỉ chạy lại nếu object ref thay đổi.

  return height;
};